function checkLegacyAmplitudeInstance() {
  if (typeof amplitude === "undefined") {
    console.warn("legacy amplitude is not available");
    return false;
  }

  return true;
}

function setNewLegacyAmplitudeParams() {
  if (!checkLegacyAmplitudeInstance()) {
    return;
  }

  const url = new URL(window.location);
  const newSession = amplitude.getInstance()._newSession;
  const utmParams = [
    "referrer",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];

  const urlContainsUTMs = function () {
    let containsUTMs = false;
    for (const param of utmParams) {
      const paramVal = url.searchParams.get(param);
      if (paramVal !== undefined) {
        containsUTMs = true;
        break;
      }
    }
    return containsUTMs;
  };

  if (urlContainsUTMs()) {
    utmParams.forEach((param) => {
      const paramVal = url.searchParams.get(param);
      const storedVal = localStorage.getItem(`_op_amp-${param}`);
      if (paramVal !== storedVal || (paramVal !== undefined && newSession)) {
        let identify;
        if (paramVal !== undefined) {
          identify = new amplitude.Identify().set(param, paramVal);
          localStorage.setItem(`_op_amp-${param}`, paramVal);
        } else {
          identify = new amplitude.Identify().unset(param);
        }
        amplitude.getInstance().identify(identify);
      }
    });
  }
}

const setLegacyAmplitudeUser = () => {
  if (!checkLegacyAmplitudeInstance()) {
    return;
  }

  if (!window.theme || !window.theme.customer) {
    return;
  }

  const { customer } = window.theme;
  const legacyAmplitudeClient = amplitude.getInstance();
  if (customer.isLoggedIn) {
    legacyAmplitudeClient.setUserId(customer.id);
    legacyAmplitudeClient.setUserProperties(customer);
  } else {
    legacyAmplitudeClient.setUserId(null);
  }
};

const setLegacyAmplitudeMarket = (data) => ({
  ...data,
  market: data.market ?? document.documentElement.dataset?.countryCode,
});
export default class LegacyAmplitude {
  EVENT_BUNDLE_ADDED = "bundle added";
  EVENT_CART_VIEWED = "click : cart viewed";
  EVENT_CLICK_ACCORDION = "click : accordion";
  EVENT_CLICK_ADD_FROM_MODULE = "click : add from module";
  EVENT_CLICK_ADD_FROM_PRODUCT_SELECTOR = "click : add from product selector";
  EVENT_CLICK_BREADCRUMB_LINK = "click : breadcrumb";
  EVENT_CLICK_CART_MODULE = "click : cart module";
  EVENT_CLICK_CONTINUE_TO_CHECKOUT = "click : continue to checkout";
  EVENT_CLICK_COUNTRY_SELECTION_MODAL = "click : country selection modal";
  EVENT_CLICK_LOCAL_NAV_LINK = "click : local navigation";
  EVENT_CLICK_MOBILE_MENU = "click : hamburger menu";
  EVENT_CLICK_MODULE = "click : module";
  EVENT_CLICK_NAV_LINK = "click : navigation bar";
  EVENT_CLICK_PRODUCT = "click : product";
  EVENT_CLICK_PRODUCT_IMAGE = "click : product image";
  EVENT_CLICK_PRODUCT_SELECTOR = "click : product combo selector";
  EVENT_CLICK_PRODUCT_SWATCH = "click : product swatch";
  EVENT_CLICK_PROMO_LINK = "click : site wide banner";
  EVENT_CLICK_QUANTITY_SELECTOR = "click : quantity selector";
  EVENT_CLICK_SIZE_SELECTOR = "click : size selector";
  EVENT_CLOSE_POPUP = "close : popup";
  EVENT_CONTACT_FORM_SUBMIT = "submit : contact us form";
  EVENT_FILL_OUT_POPUP = "fill out : popup";
  EVENT_HOVER_MODULE = "hover : module";
  EVENT_HOVER_NAV_LINK = "hover : navigation bar";
  EVENT_LOGIN_FORM_SUBMIT = "submit : login form";
  EVENT_ORDER_CREATED = "checkout : order created";
  EVENT_ORDER_SUBMITTED = "test : order submitted";
  EVENT_PRODUCT_ADDED = "product added";
  EVENT_REGISTER_EXPERIMENT = "experiment : experiment assigned";
  EVENT_SUBMIT_POPUP = "submit : popup";
  EVENT_SUBMIT_POPUP_EMAIL = "submit : popup email";
  EVENT_SUBMIT_POPUP_SMS = "submit : popup phone";
  EVENT_SUBMIT_SITE_SEARCH = "submit : site search";
  EVENT_SUCCESSFUL_SIGN_OUT = "submit : logout";
  EVENT_UPSELL_PRODUCT_ADDED = "click : add from slideout";
  EVENT_VIEW_CART_PRODUCT_VARIANT_DRAWER = "view : cart product variant drawer";
  EVENT_VIEW_CART_UPSELLS = "view : up-sell slide-out";
  EVENT_VIEW_CHECKOUT_PAGE = "test : checkout page";
  EVENT_VIEW_COUNTRY_SELECTION_MODAL = "view : country selection modal";
  EVENT_VIEW_ORDER_CONFIRMATION = "view : order confirmation";
  EVENT_VIEW_PAGE = "view : page";
  EVENT_VIEW_POPUP = "view : popup";
  EVENT_VIEW_PRODUCT_DETAILS = "view : product details";
  EVENT_VIEW_SEARCH_BAR = "view : search bar";
  EVENT_VIEW_SEARCH_RESULTS = "view : search results";

  constructor(apiKey) {
    if (checkLegacyAmplitudeInstance()) {
      this.initialize(apiKey);
      return;
    }
    this.waitForLegacyAmplitude(apiKey);
  }

  initialize(apiKey) {
    if (!checkLegacyAmplitudeInstance()) {
      return;
    }
    const options = {
      includeUtm: true,
      includeReferrer: true,
      unsetParamsReferrerOnNewSession: true,
      saveParamsReferrerOncePerSession: false,
    };
    amplitude.getInstance().init(apiKey, null, options);
    setNewLegacyAmplitudeParams();
    setLegacyAmplitudeUser();
  }

  waitForLegacyAmplitude(apiKey) {
    let attempts = 0;
    const interval = setInterval(() => {
      if (checkLegacyAmplitudeInstance() || attempts > 100) {
        clearInterval(interval);
        this.initialize(apiKey);
      }
      attempts++;
    }, 100);
  }

  get name() {
    return "LegacyAmplitude";
  }

  logEvent(event, eventPayload) {
    if (typeof amplitude === "undefined") {
      console.warn("legacy amplitude is not available");
      return;
    }
    console.info(event, eventPayload);
    amplitude
      .getInstance()
      .logEvent(event, setLegacyAmplitudeMarket(eventPayload));
  }

  clickProduct(data) {
    this.logEvent(this.EVENT_CLICK_PRODUCT, data);
  }

  clickAccordion(data) {
    this.logEvent(this.EVENT_CLICK_ACCORDION, data);
  }

  clickAddFromModule(data) {
    this.logEvent(this.EVENT_CLICK_ADD_FROM_MODULE, data);
  }

  clickAddFromProductSelector(data) {
    this.logEvent(this.EVENT_CLICK_ADD_FROM_PRODUCT_SELECTOR, data);
  }

  clickBreadcrumbLink(data) {
    this.logEvent(this.EVENT_CLICK_BREADCRUMB_LINK, data);
  }

  clickProductImage(data) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_IMAGE, data);
  }

  clickHamburgerMenu(data) {
    this.logEvent(this.EVENT_CLICK_MOBILE_MENU, data);
  }

  clickProductSelector(data) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_SELECTOR, data);
  }

  clickProductSwatch(data) {
    this.logEvent(this.EVENT_CLICK_PRODUCT_SWATCH, data);
  }

  clickPromoLink(data) {
    this.logEvent(this.EVENT_CLICK_PROMO_LINK, data);
  }

  clickQuantitySelector(data) {
    this.logEvent(this.EVENT_CLICK_QUANTITY_SELECTOR, data);
  }

  clickSizeSelector(data) {
    this.logEvent(this.EVENT_CLICK_SIZE_SELECTOR, data);
  }

  continueToCheckout(data) {
    this.logEvent(this.EVENT_CLICK_CONTINUE_TO_CHECKOUT, data);
  }

  clickLocalNavLink(data) {
    this.logEvent(this.EVENT_CLICK_LOCAL_NAV_LINK, data);
  }

  clickNavLink(data) {
    this.logEvent(this.EVENT_CLICK_NAV_LINK, data);
  }

  hoverNavLink(data) {
    this.logEvent(this.EVENT_HOVER_NAV_LINK, data);
  }

  viewPage(data) {
    this.logEvent(this.EVENT_VIEW_PAGE, data);
  }

  viewPopup(data) {
    this.logEvent(this.EVENT_VIEW_POPUP, data);
  }

  viewProductDetails(data) {
    this.logEvent(this.EVENT_VIEW_PRODUCT_DETAILS, data);
  }

  viewCartProductVariantDrawer(data) {
    this.logEvent(this.EVENT_VIEW_CART_PRODUCT_VARIANT_DRAWER, data);
  }

  viewOrderConfirmation(data) {
    this.logEvent(this.EVENT_VIEW_ORDER_CONFIRMATION, data);
  }

  viewCheckoutPage(data) {
    this.logEvent(this.EVENT_VIEW_CHECKOUT_PAGE, data);
  }

  viewCartUpsells(data) {
    this.logEvent(this.EVENT_VIEW_CART_UPSELLS, data);
  }

  fillOutPopup(data) {
    this.logEvent(this.EVENT_FILL_OUT_POPUP, data);
  }

  submitPopup(data) {
    this.logEvent(this.EVENT_SUBMIT_POPUP, data);
  }

  productAdded(data) {
    this.logEvent(this.EVENT_PRODUCT_ADDED, data);
  }

  clickAddFromSlideout(data) {
    this.logEvent(this.EVENT_UPSELL_PRODUCT_ADDED, data);
  }

  orderCreated(data) {
    this.logEvent(this.EVENT_ORDER_CREATED, data);
  }

  orderSubmitted(data) {
    this.logEvent(this.EVENT_ORDER_SUBMITTED, data);
  }

  submitContactForm(data) {
    this.logEvent(this.EVENT_CONTACT_FORM_SUBMIT, data);
  }

  cartViewed(data) {
    this.logEvent(this.EVENT_CART_VIEWED, data);
  }

  submitLoginForm(data) {
    this.logEvent(this.EVENT_LOGIN_FORM_SUBMIT, data);
  }

  successfulSignOut(data) {
    this.logEvent(this.EVENT_SUCCESSFUL_SIGN_OUT, data);
  }

  viewCountrySelectionModal(data) {
    this.logEvent(this.EVENT_VIEW_COUNTRY_SELECTION_MODAL, data);
  }

  clickCountrySelectionModal(data) {
    this.logEvent(this.EVENT_CLICK_COUNTRY_SELECTION_MODAL, data);
  }

  loadedExperiment(data) {
    const registeredExperiments =
      (window.localStorage.registeredExperiments &&
        JSON.parse(window.localStorage.registeredExperiments)) ||
      [];

    if (
      registeredExperiments.filter(
        (ex) => JSON.stringify(ex) === JSON.stringify(data)
      ).length === 0
    ) {
      const legacyAmplitudeLoop = setInterval(() => {
        this.logEvent(this.EVENT_REGISTER_EXPERIMENT, data);
        registeredExperiments.push(data);
        window.localStorage.setItem(
          "registeredExperiments",
          JSON.stringify(registeredExperiments)
        );
        clearInterval(legacyAmplitudeLoop);
      }, 100);
    }
  }

  attentiveSubmitPopupEmail(data) {
    this.logEvent(this.EVENT_SUBMIT_POPUP_EMAIL, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveSubmitPopupSMS(data) {
    this.logEvent(this.EVENT_SUBMIT_POPUP_SMS, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveViewPopup(data) {
    this.logEvent(this.EVENT_VIEW_POPUP, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  attentiveClosePopup(data) {
    this.logEvent(this.EVENT_CLOSE_POPUP, {
      "event label": data.eventCategory,
      variant: data.eventLabel,
    });
  }

  clickModule(data) {
    this.logEvent(this.EVENT_CLICK_MODULE, data);
  }

  clickCartModule(data) {
    this.logEvent(this.EVENT_CLICK_CART_MODULE, data);
  }

  hoverModule(data) {
    this.logEvent(this.EVENT_HOVER_MODULE, data);
  }

  submitSiteSearch(data) {
    this.logEvent(this.EVENT_SUBMIT_SITE_SEARCH, data);
  }

  viewSearchBar(data) {
    this.logEvent(this.EVENT_VIEW_SEARCH_BAR, data);
  }

  viewSearchResults(data) {
    this.logEvent(this.EVENT_VIEW_SEARCH_RESULTS, data);
  }
}
